import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { observer } from 'mobx-react';
import {ImagePreloader} from '../types/fn';
import {url} from 'csx';
import {deps, inject, StatefulComponent} from '../lib/component';

export interface ResponsiveImageProps {
  identity: string;
  preload: ImagePreloader;
  className?: string;
  style?: any;
  Element?: any;
  href?: string;
  asBackground?: boolean;
  dangerouslySetInnerHTML?: any;
  onClick?(event: React.MouseEvent<any>): any;
  wrapBackground?(bg: string): string;
  sizeTarget?: any;
  defaultSize?: {
    width: number;
    height: number;
    type?: string;
    placeholder?: boolean;
  }
}

export interface ResponsiveImageState {
  src: string;
  loaded: boolean;
}

@inject(deps) @observer
export class ResponsiveImage extends StatefulComponent<ResponsiveImageProps, ResponsiveImageState> {
  private _mounted: boolean;
  private $el: any;

  static defaultProps = {
    defaultSize: { width: 64, height: 64, placeholder: true }
  };

  constructor(props: ResponsiveImageProps) {
    super(props);
    this.state = ResponsiveImage.getDefaultState(props);
  };

  static getDefaultState = (props: ResponsiveImageProps) => ({
    src: props.preload(props.identity, props.defaultSize),
    loaded: false,
  });

  render() {
    const { model, controller, identity, preload, defaultSize, style = {}, asBackground, wrapBackground, Element, ...otherProps } = this.props;
    const { imageLoadIndex } = model.ui;

    if (asBackground) {
      const backgroundImage = wrapBackground
        ? wrapBackground(url(this.state.src))
        : url(this.state.src);

      return (
        <div ref={el => this.$el = el} {...otherProps} style={{...style, backgroundImage}}/>
      );
    };

    const props = {
      ...otherProps,
      style,
      src: this.state.src
    };

    if (!Element) return (
      <img ref={el => this.$el = el} {...props} />
    );

    return (
      <Element ref={el => this.$el = el} {...props} />
    )
  }

  componentDidMount() {
    this._mounted = true;
    this.loadImage();
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  componentWillReceiveProps(nextProps: ResponsiveImageProps) {
    if (this.props.identity !== nextProps.identity) {
      this.setState(ResponsiveImage.getDefaultState(nextProps));
      this.loadImage();
    }
  }

  componentDidUpdate() {
    this.loadImage();
  }

  loadImage = () => setTimeout(() => {
    if (!this._mounted || this.state.loaded)
      return;

    const node = ReactDOM.findDOMNode(this.props.sizeTarget || this.$el);

    if (node && ResponsiveImage.shouldLoad(node)) {
      const style = getComputedStyle(node);
      const width = Math.ceil(parseFloat(style.width));
      const height = Math.ceil(parseFloat(style.height));

      const widthLargest = width >= height;

      const src = this.props.preload(this.props.identity, {
        width: widthLargest ? width : 0,
        height: widthLargest ? 0 : height,
        type: this.props.defaultSize.type
      });

      const img = new Image();
      img.onload = () => this._mounted && this.setState({src, loaded: true});
      img.src = src;
    }
  }, 0);

  static shouldLoad = (el: Element, distance = 0) => {
    const {innerWidth, innerHeight} = window;
    const {left, right, top, bottom} = el.getBoundingClientRect();

    return left - distance < innerWidth
      && right + distance > 0
      && top - distance < innerHeight
      && bottom + distance > 0;
  };

  static i18n = {};
  static styles = {};
}